/* eslint-disable no-unused-vars */
import { useState, useRef, useEffect } from 'react';
import { supabase } from './supabaseClient';
import Login from './pages/Login';
import './App.css';
import lemonTreeLogo from './assets/lemon_tree_transparent.png';
import lemonSliceIcon from './assets/lemon-slice.png';
import {
  Home, ClipboardList, ArrowLeftRight, Package, BarChart2, ChevronUp,
  ClipboardCheck, ShieldAlert, Users, Calendar, FileText, GraduationCap,
  TrendingUp, ChevronDown, Wrench, Thermometer, PanelLeftClose, PanelLeftOpen,
  Database, Shield, MessageSquare, Star, Edit, RefreshCw, ChefHat, Wallet,
  BookOpen,
} from 'lucide-react';
import Orders from './pages/Orders';
import Transfers from './pages/Transfers';
import Stock from './pages/Stock';
import Reports from './pages/Reports';
import ResultsPage from './pages/Results';
import SettingsPage from './pages/Settings';
import Products from './pages/Products';
import ParLevels from './pages/ParLevels';
import Checklist from './pages/Checklist';
import ChecklistAdmin from './pages/ChecklistAdmin';
import Allergens from './pages/Allergens';
import Team from './pages/Team';
import TrainingPage from './pages/Training';
import CalendarPage from './pages/Calendar';
import DocsPage from './pages/Docs';
import MaintenancePage from './pages/Maintenance';
import FridgeTempsPage from './pages/FridgeTemps';
import NewHireForm from './pages/NewHireForm';
import Onboarding from './pages/Onboarding';
import Warning from './pages/WarningConfirmation';
import UserManagement from './pages/UserManagement';
import SetPassword from './pages/SetPassword';
import Homepage from './pages/Homepage';
import ProductOrder from './pages/ProductOrder';
import DataEntryPage from './pages/DataEntry';
import GovernancePage from './pages/Governance';
import { CashUpTab, CashReportTab } from './pages/CashUp';
import ShiftyPage from './pages/Shifty';
import KitchenShiftyPage from './pages/KitchenShifty';
import BalancePage from './pages/BalancePage';
import GroupResults from './pages/GroupResults';
import Events from './pages/Events';
import EventEnquiry from './pages/EventEnquiry';
import ShareholderView from './pages/ShareholderView';
import AllergensPublic from './pages/AllergensPublic.js';
import DataSync from './pages/DataSync';
import FinancePage from './pages/Finance';
import ReviewsPage from './pages/Reviews';
import { isAdminUser, isManagerUser, isSupervisorUser, venueDisplayLabel } from './userUtils';
import { useRouter } from './hooks/useRouter';

// ── Staff nav ─────────────────────────────────────────────────────────────────
const STAFF_NAV = [
  { key: 'home',      label: 'Home',           icon: Home          },
  { key: 'checklist', label: 'Daily Checklist', icon: ClipboardCheck},
  { key: 'orders',    label: 'Orders',          icon: ClipboardList },
  { key: 'transfers', label: 'Transfers',       icon: ArrowLeftRight},
  { key: 'reviews',   label: 'Reviews',         icon: Star          },
  { key: 'allergens', label: 'Allergens',       icon: ShieldAlert   },
  { key: 'training',  label: 'Training',        icon: GraduationCap },
  { key: 'calendar',  label: 'Calendar',        icon: Calendar      },
];

const PREP_KITCHEN_NAV = [
  { key: 'home',      label: 'Home',           icon: Home          },
  { key: 'checklist', label: 'Daily Checklist', icon: ClipboardCheck},
  { key: 'stocktake', label: 'Stocktake',       icon: Package       },
  { key: 'transfers', label: 'Transfers',       icon: ArrowLeftRight},
  { key: 'allergens', label: 'Allergens',       icon: ShieldAlert   },
  { key: 'training',  label: 'Training',        icon: GraduationCap },
  { key: 'calendar',  label: 'Calendar',        icon: Calendar      },
];

// ── Supervisor primary nav (unchanged) ───────────────────────────────────────
const SUPERVISOR_NAV_PRIMARY = [
  { key: 'home',      label: 'Home',      icon: Home          },
  { key: 'checklist', label: 'Checklist', icon: ClipboardCheck },
  { key: 'shifty',    label: 'Shifty',    icon: MessageSquare },
  { key: 'reviews',   label: 'Reviews',   icon: Star          },
  { key: 'reports',   label: 'Reports',   icon: BarChart2     },
  { key: 'team',      label: 'Team',      icon: Users         },
  { key: 'training',  label: 'Training',  icon: GraduationCap },
  { key: 'calendar',  label: 'Calendar',  icon: Calendar      },
  { key: 'docs',      label: 'Info Hub',  icon: FileText      },
];

// ── Ops nav — supervisor (unchanged) ─────────────────────────────────────────
const SUPERVISOR_NAV_OPS = [
  { key: 'orders',      label: 'Orders',      icon: ClipboardList  },
  { key: 'transfers',   label: 'Transfers',   icon: ArrowLeftRight },
  { key: 'maintenance', label: 'Maintenance', icon: Wrench         },
  { key: 'allergens',   label: 'Allergens',   icon: ShieldAlert    },
];

// ── Grouped nav — admin/manager ───────────────────────────────────────────────

// Admin pinned
const ADMIN_PINNED_NAV = [
  { key: 'home',        label: 'Home',        icon: Home       },
  { key: 'results',     label: 'Results',     icon: TrendingUp },
  { key: 'reviews',     label: 'Reviews',     icon: Star       },
  { key: 'team',        label: 'Team',        icon: Users      },
  { key: 'calendar',    label: 'Calendar',    icon: Calendar   },
  { key: 'maintenance', label: 'Maintenance', icon: Wrench     },
];

// Manager pinned
const MANAGER_PINNED_NAV = [
  { key: 'home',           label: 'Home',           icon: Home          },
  { key: 'shifty',         label: 'Shifty',         icon: MessageSquare },
  { key: 'kitchen-shifty', label: 'Kitchen Shifty', icon: ChefHat       },
  { key: 'team',           label: 'Team',           icon: Users         },
  { key: 'reviews',        label: 'Reviews',        icon: Star          },
  { key: 'events',         label: 'Events',         icon: Star          },
  { key: 'training',       label: 'Training',       icon: GraduationCap },
  { key: 'calendar',       label: 'Calendar',       icon: Calendar      },
];

// Fin group
const ADMIN_NAV_FIN = [
  { key: 'group-results', label: 'Group',      icon: Database  },
  { key: 'governance',    label: 'Governance', icon: Shield    },
  { key: 'cashup',        label: 'Cash Up',    icon: Wallet    },
  { key: 'finance',       label: 'Finance',    icon: BookOpen  },
  { key: 'docs',          label: 'Info Hub',   icon: FileText  },
  { key: 'data-entry',    label: 'Data Entry', icon: Edit      },
  { key: 'data-sync',     label: 'Data Sync',  icon: RefreshCw },
];
const MANAGER_NAV_FIN = [
  { key: 'results', label: 'Results',  icon: TrendingUp },
  { key: 'docs',    label: 'Info Hub', icon: FileText   },
];

// Advisor nav — fin read-only
const ADVISOR_NAV = [
  { key: 'home',          label: 'Home',       icon: Home      },
  { key: 'results',       label: 'Results',    icon: TrendingUp },
  { key: 'group-results', label: 'Group',      icon: Database  },
  { key: 'governance',    label: 'Governance', icon: Shield    },
];

// HR group (admin only)
const ADMIN_NAV_HR = [
  { key: 'training',       label: 'Training',        icon: GraduationCap },
  { key: 'shifty',         label: 'Shifty',          icon: MessageSquare },
  { key: 'kitchen-shifty', label: 'Kitchen Shifty',  icon: ChefHat       },
];

// Ops group
const ADMIN_NAV_OPS = [
  { key: 'orders',    label: 'Orders',    icon: ClipboardList  },
  { key: 'transfers', label: 'Transfers', icon: ArrowLeftRight },
  { key: 'reports',   label: 'Reports',   icon: BarChart2      },
  { key: 'events',    label: 'Events',    icon: Star           },
  { key: 'allergens', label: 'Allergens', icon: ShieldAlert    },
];
const MANAGER_NAV_OPS = [
  { key: 'orders',    label: 'Orders',    icon: ClipboardList  },
  { key: 'transfers', label: 'Transfers', icon: ArrowLeftRight },
  { key: 'reports',   label: 'Reports',   icon: BarChart2      },
  { key: 'allergens', label: 'Allergens', icon: ShieldAlert    },
];

// ── Profile popup dropdowns ───────────────────────────────────────────────────
const ADMIN_DROPDOWN = [
  { key: 'checklists',    label: 'Checklist Templates' },
  { key: 'fridgetemps',   label: 'Fridge Temps'        },
  { key: 'products',      label: 'Products'            },
  { key: 'product-order', label: 'Product Order'       },
  { key: 'users',         label: 'Users'               },
  { key: 'settings',      label: 'Settings'            },
];

const MANAGER_DROPDOWN = [
  { key: 'checklists',    label: 'Checklist Templates' },
  { key: 'fridgetemps',   label: 'Fridge Temps'        },
  { key: 'products',      label: 'Products'            },
  { key: 'product-order', label: 'Product Order'       },
  { key: 'settings',      label: 'Settings'            },
];

const SUPERVISOR_DROPDOWN = [];

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

function compressOrder(r) {
  const totalValue = r.items.reduce((sum, i) =>
    sum + ((i.quantityFulfilled ?? i.quantity) * (i.price || 0)), 0
  );
  const value = Math.round(totalValue * 100) / 100;
  if (value === 0) return [];
  const hasBar   = r.items.some(i => i.category === 'Bar');
  const category = hasBar ? 'Bar' : 'Kitchen';
  const logs = [{ date: r.date, venue: r.venue, category, value }];
  if (r.orderFrom !== 'Prep Kitchen') {
    logs.push({ date: r.date, venue: r.orderFrom, category, value: -value });
  }
  return logs;
}

function normalizeProduct(p, suppliers = []) {
  return {
    ...p,
    fulfilmentType:   p.fulfilment_type   || 'external',
    availableAt:      p.available_at      || [],
    orderFromVenues:  p.order_from_venues || [],
    defaultFulfiller: p.default_fulfiller || 'Prep Kitchen',
    stockTracked:     p.stock_tracked     || false,
    stocktakeDays:    p.stocktake_days    || [],
    supplierId:       p.supplier_id,
    supplierName:     suppliers.find(s => s.id === p.supplier_id)?.name || '',
  };
}

// ── NavGroup — collapsible section header + children ─────────────────────────
function NavGroup({ label, expanded, onToggle, collapsed, isMobile, children }) {
  return (
    <div style={{ marginTop: '0.5rem' }}>
      {(!collapsed || isMobile) && (
        <button
          onClick={onToggle}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', padding: '0.4rem 0.85rem', background: 'none',
            border: 'none', cursor: 'pointer', color: '#aaa',
            fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
          }}
        >
          <span>{label}</span>
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      )}
      {(expanded || (collapsed && !isMobile)) && children}
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ activePage, onNavigate, currentUser, onLogout, collapsed, onToggleCollapse, isMobile, onClose, orderCartCount }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [finExpanded,  setFinExpanded]  = useState(false);
  const [hrExpanded,   setHrExpanded]   = useState(false);
  const [opsExpanded,  setOpsExpanded]  = useState(false);
  const dropdownRef = useRef(null);

  const isAdmin      = isAdminUser(currentUser);
  const isManager    = currentUser.role === 'manager';
  const isSupervisor = currentUser.role === 'supervisor';
  const isAdvisor    = currentUser.role === 'advisor';
  const isElevated   = isAdmin || isManager || isSupervisor;
  const isPrepKitchen = currentUser.venue === 'Prep Kitchen';

  const finNav        = isAdmin ? ADMIN_NAV_FIN : MANAGER_NAV_FIN;
  const hrNav         = ADMIN_NAV_HR;
  const opsNav        = isAdmin ? ADMIN_NAV_OPS : isSupervisor ? SUPERVISOR_NAV_OPS : MANAGER_NAV_OPS;
  const pinnedNav     = isAdmin ? ADMIN_PINNED_NAV : MANAGER_PINNED_NAV;
  const primaryNav    = isSupervisor ? SUPERVISOR_NAV_PRIMARY : null;
  const dropdownItems = isAdmin ? ADMIN_DROPDOWN
    : isManager       ? MANAGER_DROPDOWN
    : SUPERVISOR_DROPDOWN;

  const staffNav  = isPrepKitchen ? PREP_KITCHEN_NAV : STAFF_NAV;
  const homePage  = 'home';
  const roleLabel = isAdmin ? 'Admin' : isManager ? 'Manager' : isSupervisor ? 'Supervisor' : isAdvisor ? 'Advisor' : null;

  // Auto-expand the group containing the active page
  useEffect(() => {
    if (!isAdmin && !isManager) return;
    if (finNav.some(i => i.key === activePage))  setFinExpanded(true);
    if (hrNav.some(i => i.key === activePage))   setHrExpanded(true);
    if (opsNav.some(i => i.key === activePage))  setOpsExpanded(true);
  }, [activePage, isAdmin, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = currentUser.name
    ? currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase()
    : '?';

  function NavItem({ item }) {
    const Icon     = item.icon;
    const isActive = activePage === item.key;
    return (
      <button
        className={`nav-item${isActive ? ' active' : ''}`}
        onClick={() => {
          // Warn if leaving Orders with items in cart
          if (activePage === 'orders' && item.key !== 'orders' && orderCartCount > 0) {
            const ok = window.confirm(`You have ${orderCartCount} item${orderCartCount !== 1 ? 's' : ''} in your cart that haven't been submitted. Your draft is saved — come back to finish it.`);
            if (!ok) return;
          }
          onNavigate(item.key);
          if (isMobile) onClose();
        }}
        title={collapsed && !isMobile ? item.label : undefined}
        style={collapsed && !isMobile ? { justifyContent: 'center', padding: '0.6rem 0' } : undefined}
      >
        <Icon size={18} strokeWidth={1.8} />
        {(!collapsed || isMobile) && <span>{item.label}</span>}
      </button>
    );
  }

  const LemonIcon = ({ size = 28 }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 24 C8 14 14 8 24 8 C34 8 40 14 40 24 C40 34 34 40 24 40 C14 40 8 34 8 24 Z" fill="#F7EC71" stroke="#E4AC49" strokeWidth="2"/>
      <path d="M8 24 C6 21 4 22 3 24 C4 26 6 27 8 24 Z" fill="#F7EC71" stroke="#E4AC49" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M40 24 C42 21 44 22 45 24 C44 26 42 27 40 24 Z" fill="#F7EC71" stroke="#E4AC49" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M28 10 C30 6 36 6 34 10 C32 8 30 8 28 10 Z" fill="#7EA594" stroke="#5a8a78" strokeWidth="1"/>
      <ellipse cx="19" cy="19" rx="5" ry="3" fill="rgba(255,255,255,0.35)" transform="rotate(-30 19 19)"/>
    </svg>
  );

  const sidebarStyle = isMobile ? {
    position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 1000,
    transform: 'translateX(0)',
    boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
    width: '240px', minWidth: '240px',
  } : collapsed ? { width: '60px', minWidth: '60px' } : { width: '220px', minWidth: '220px' };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
        }}/>
      )}

      <div className="sidebar" style={sidebarStyle}>
        <div className="sidebar-logo" onClick={() => {
            if (activePage === 'orders' && orderCartCount > 0) {
              const ok = window.confirm(`You have ${orderCartCount} item${orderCartCount !== 1 ? 's' : ''} in your cart that haven't been submitted. Your draft is saved — come back to finish it.`);
              if (!ok) return;
            }
            onNavigate(homePage); if (isMobile) onClose();
          }}
          style={{ cursor: 'pointer', justifyContent: collapsed && !isMobile ? 'center' : undefined }}>
          {(!collapsed || isMobile) && <img src={lemonTreeLogo} alt="Lemon Tree" />}
          {collapsed && !isMobile && <LemonIcon size={28} />}
        </div>

        <nav className="sidebar-nav">
          {(isAdmin || isManager) ? (            <>
              {/* Pinned items */}
              {pinnedNav.map(item => (
                <NavItem key={item.key} item={item} />
              ))}

              {/* HR group — admin only */}
              {isAdmin && (
                <NavGroup label="HR" expanded={hrExpanded} onToggle={() => setHrExpanded(p => !p)} collapsed={collapsed} isMobile={isMobile}>
                  {hrNav.map(item => <NavItem key={item.key} item={item} />)}
                </NavGroup>
              )}

              {/* Ops group */}
              <NavGroup label="Ops" expanded={opsExpanded} onToggle={() => setOpsExpanded(p => !p)} collapsed={collapsed} isMobile={isMobile}>
                {opsNav.map(item => <NavItem key={item.key} item={item} />)}
              </NavGroup>

              {/* Fin group */}
              <NavGroup label="Fin" expanded={finExpanded} onToggle={() => setFinExpanded(p => !p)} collapsed={collapsed} isMobile={isMobile}>
                {finNav.map(item => <NavItem key={item.key} item={item} />)}
              </NavGroup>
            </>
          ) : isSupervisor ? (
            <>
              {primaryNav.map(item => (
                <NavItem key={item.key} item={item} />
              ))}
              <div style={{ marginTop: '0.5rem' }}>
                {(!collapsed || isMobile) && (
                  <button
                    onClick={() => setOpsExpanded(p => !p)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', padding: '0.4rem 0.85rem', background: 'none',
                      border: 'none', cursor: 'pointer', color: '#aaa',
                      fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}
                  >
                    <span>Ops</span>
                    {opsExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                )}
                {(opsExpanded || (collapsed && !isMobile)) && opsNav.map(item => (
                  <NavItem key={item.key} item={item} />
                ))}
              </div>
            </>
          ) : isAdvisor ? (
            ADVISOR_NAV.map(item => (
              <NavItem key={item.key} item={item} />
            ))
          ) : (
            staffNav.map(item => (
              <NavItem key={item.key} item={item} />
            ))
          )}
        </nav>

        <div className="sidebar-bottom">
          {/* Desktop collapse toggle only */}
          {!isMobile && (
            <button
              onClick={onToggleCollapse}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{
                width: '100%', padding: '0.4rem 0',
                background: 'none', border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer', color: '#aaa',
                display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-end',
                paddingRight: collapsed ? 0 : '0.75rem',
                transition: 'all 0.2s',
              }}>
              {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
            </button>
          )}

          <div ref={dropdownRef}>
            {dropdownOpen && (
              <div className="profile-dropdown">
                {dropdownItems.map(({ key, label }) => (
                  <div
                    key={key}
                    className="profile-dropdown-item"
                    onClick={() => {
                      if (activePage === 'orders' && orderCartCount > 0) {
                        const ok = window.confirm(`You have ${orderCartCount} item${orderCartCount !== 1 ? 's' : ''} in your cart that haven't been submitted. Your draft is saved — come back to finish it.`);
                        if (!ok) return;
                      }
                      onNavigate(key); setDropdownOpen(false); if (isMobile) onClose();
                    }}
                  >
                    {label}
                  </div>
                ))}
                <div
                  className="profile-dropdown-item"
                  onClick={onLogout}
                  style={{ color: '#cc0000' }}
                >
                  Logout
                </div>
              </div>
            )}
            <div
              className="sidebar-profile"
              onClick={() => setDropdownOpen(p => !p)}
              title={collapsed && !isMobile ? currentUser.name : undefined}
              style={collapsed && !isMobile ? { justifyContent: 'center', padding: '0.5rem 0' } : undefined}
            >
              <div className="profile-avatar" style={{ background: '#BCD9E5', color: '#2F3A42' }}>{initials}</div>
              {(!collapsed || isMobile) && (
                <>
                  <div className="profile-info">
                    <div className="profile-name">{currentUser.name}</div>
                    <div className="profile-venue">{venueDisplayLabel(currentUser)}</div>
                    {roleLabel && <div className="profile-role" style={{ color: '#3A8FA3' }}>{roleLabel}</div>}
                  </div>
                  <ChevronUp size={14} style={{ color: '#aaa', transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
function App() {
  const [currentUser,      setCurrentUser]      = useState(null);
  const [authLoading,      setAuthLoading]      = useState(true);
  const [prefilledCart,    setPrefilledCart]    = useState(null);
  const [products,         setProducts]         = useState([]);
  const [productsLoading,  setProductsLoading]  = useState(true);
  const [parLevels,        setParLevels]        = useState({});
  const [checklistLog,     setChecklistLog]     = useState([]);
  const [requests,         setRequestsRaw]      = useState([]);
  const [transferLog,      setTransferLog]      = useState([]);
  const [stockLog,         setStockLog]         = useState([]);
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Tracks how many items are in the Orders cart so Sidebar can warn before navigating away
  const [orderCartCount,   setOrderCartCount]   = useState(0);

  // ── Router ────────────────────────────────────────────────────────────────
  const { activePage, navigate, hasUrlIntent } = useRouter();

  // Reset cart count badge when user navigates away from Orders
  useEffect(() => {
    if (activePage !== 'orders') setOrderCartCount(0);
  }, [activePage]);

  // ── Public routes — bypass auth entirely ──────────────────────────────────
  const onboardingMatch = window.location.pathname.match(/^\/onboarding\/([a-z0-9]+)$/);
  const welcomeMatch    = window.location.pathname.match(/^\/welcome\/([a-z0-9]+)$/);
  const balanceMatch    = window.location.pathname.match(/^\/balance\/([a-z0-9]+)$/);
  const warningMatch    = window.location.pathname.match(/^\/warning\/([a-z0-9]+)$/);
  const isEventEnquiry    = window.location.pathname === '/event-enquiry';
  const isAllergensPublic = window.location.pathname === '/allergens-public';

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.location.pathname.startsWith('/set-password')) {
      setAuthLoading(false);
      return;
    }

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const { data: profile } = await supabase
          .from('profiles').select('*').eq('id', session.user.id).single();
        if (profile) {
          setCurrentUser(profile);
        }
      }
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') setCurrentUser(null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Default landing page (only when no URL intent) ────────────────────────
  useEffect(() => {
    const path = window.location.pathname;
    const isBypass = /^\/(balance|onboarding|welcome|warning)\//.test(path) || path === '/event-enquiry' || path === '/allergens-public' || path.startsWith('/governance/share');
    if (!currentUser || hasUrlIntent || isBypass) return;
    if (activePage !== null) return;

    if (isAdminUser(currentUser)) {
      navigate('home');
    } else if (['manager', 'supervisor'].includes(currentUser.role)) {
      navigate('home');
    } else {
      navigate('home');
    }
  }, [currentUser, activePage, hasUrlIntent, navigate]);

  // ── Role guard ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser || !activePage) return;

    const role = currentUser.role;

    const accessMap = {
      home:             true,
      checklist:        true,
      stocktake:        true,
      orders:           true,
      transfers:        true,
      allergens:        true,
      training:         true,
      calendar:         true,
      team:             true,
      fridgetemps:      true,
      maintenance:      true,
      events:           true, // all roles — Events.js handles view filtering internally
      reports:          ['admin', 'manager', 'supervisor'].includes(role),
      results:          ['admin', 'manager', 'advisor'].includes(role),
      reviews:          true,
      finance:          role === 'admin',
      'group-results':  ['admin', 'advisor'].includes(role),
      governance:       ['admin', 'advisor'].includes(role),
      shifty:           ['admin', 'manager', 'supervisor'].includes(role),
      'kitchen-shifty': ['admin', 'manager', 'supervisor', 'kitchen'].includes(role),
      docs:             ['admin', 'manager', 'supervisor'].includes(role),
      'data-entry':     role === 'admin',
      checklists:       ['admin', 'manager'].includes(role),
      products:         ['admin', 'manager'].includes(role),
      'product-order':  ['admin', 'manager'].includes(role),
      users:            role === 'admin',
      settings:         ['admin', 'manager'].includes(role),
      'data-sync':      role === 'admin',
    };

    const hasAccess = accessMap[activePage];

    if (hasAccess === false) {
      navigate('home');
    }
  }, [currentUser, activePage, navigate]);

  // ── Products ──────────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadProducts() {
      const [{ data: productData, error: productError }, { data: supplierData }] = await Promise.all([
        supabase.from('products').select('*').order('name'),
        supabase.from('suppliers').select('id, name').eq('active', true),
      ]);
      if (!productError && productData) {
        const suppliers = supplierData || [];
        setProducts(productData.map(p => normalizeProduct(p, suppliers)));
      }
      setProductsLoading(false);
    }
    loadProducts();
  }, []);

  function setProductsSafe(updater) {
    setProducts(prev => typeof updater === 'function' ? updater(prev) : updater);
  }

  // ── Par levels ────────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadParLevels() {
      const { data, error } = await supabase.from('par_levels').select('*');
      if (!error && data) {
        const structured = {};
        data.forEach(row => {
          if (!structured[row.venue]) structured[row.venue] = {};
          const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
          const dayStr = DAYS[row.day_of_week];
          if (!structured[row.venue][dayStr]) structured[row.venue][dayStr] = {};
          structured[row.venue][dayStr][row.product_id] = row.par_level;
        });
        setParLevels(structured);
      }
    }
    loadParLevels();
  }, []);

  // ── Checklist log ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadChecklistLog() {
      const { data, error } = await supabase
        .from('checklist_log').select('*').order('submitted_at', { ascending: false });
      if (!error && data) {
        setChecklistLog(data.map(e => ({
          ...e,
          submittedAt: e.submitted_at,
          staffName:   e.staff_name,
          type:        e.shift,
          complete:    e.items?.every(i => i.checked) || false,
        })));
      }
    }
    loadChecklistLog();
  }, []);

  // ── Requests + realtime ───────────────────────────────────────────────────
  useEffect(() => {
    async function loadRequests() {
      const { data, error } = await supabase
        .from('requests').select('*').order('created_at', { ascending: false });
      if (!error && data) {
        setRequestsRaw(data.map(r => ({
          ...r,
          orderFrom:  r.order_from,
          sentAt:     r.sent_at,
          receivedAt: r.received_at,
        })));
      }
    }
    loadRequests();

    const channel = supabase
      .channel('requests-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'requests' },
        payload => {
          if (payload.eventType === 'INSERT') {
            const r = payload.new;
            setRequestsRaw(prev => {
              if (prev.find(x => x.id === r.id)) return prev;
              return [{ ...r, orderFrom: r.order_from, sentAt: r.sent_at, receivedAt: r.received_at }, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            const r = payload.new;
            setRequestsRaw(prev => prev.map(x =>
              x.id === r.id ? { ...r, orderFrom: r.order_from, sentAt: r.sent_at, receivedAt: r.received_at } : x
            ));
          } else if (payload.eventType === 'DELETE') {
            setRequestsRaw(prev => prev.filter(x => x.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  // ── Transfer log ──────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadTransferLog() {
      const { data, error } = await supabase
        .from('transfer_log').select('*').order('date', { ascending: false });
      if (!error && data) setTransferLog(data);
    }
    loadTransferLog();
  }, []);

  // ── Stock log ─────────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadStockLog() {
      const { data, error } = await supabase
        .from('stock_log').select('*').order('date', { ascending: false });
      if (!error && data) setStockLog(data);
    }
    loadStockLog();
  }, []);

  // ── Compress old orders ───────────────────────────────────────────────────
  useEffect(() => {
    async function compress() {
      const now = Date.now();
      const toCompress = requests.filter(r =>
        r.status === 'received' &&
        r.receivedAt &&
        (now - new Date(r.receivedAt).getTime()) > THREE_DAYS_MS
      );
      if (toCompress.length === 0) return;
      const newLogs = toCompress.flatMap(compressOrder);
      for (const log of newLogs) {
        await supabase.from('transfer_log').insert(log);
      }
      setTransferLog(prev => [...prev, ...newLogs]);
      setRequestsRaw(prev => prev.filter(r => !toCompress.find(c => c.id === r.id)));
    }
    compress();
  }, [requests]);

  // ── Trim old checklist entries ────────────────────────────────────────────
  useEffect(() => {
    const cutoff = Date.now() - (28 * 24 * 60 * 60 * 1000);
    setChecklistLog(prev => prev.filter(e => new Date(e.submittedAt).getTime() > cutoff));
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  async function handleRequestsUpdate(updatedRequests) {
    const asArray = Array.isArray(updatedRequests)
      ? updatedRequests
      : requests.map(r => r.id === updatedRequests.id ? updatedRequests : r);

    const newlyEnRoute = asArray.filter(r =>
      (r.status === 'en-route' || r.status === 'partially fulfilled') &&
      r.orderFrom === 'Prep Kitchen' &&
      requests.find(old => old.id === r.id && old.status === 'pending')
    );

    if (newlyEnRoute.length > 0) {
      setProducts(prev => prev.map(product => {
        if (!product.stockTracked) return product;
        let deduction = 0;
        newlyEnRoute.forEach(order => {
          const item = order.items.find(i => i.name === product.name);
          if (item) deduction += (item.quantityFulfilled ?? item.quantity);
        });
        return deduction > 0
          ? { ...product, stock: Math.max(0, product.stock - deduction) }
          : product;
      }));
    }

    const amended = asArray.filter(r =>
      r.status === 'received' &&
      r.amended &&
      requests.find(old => old.id === r.id && old.status === 'received')
    );
    if (amended.length > 0) {
      setTransferLog(prev => {
        const withoutAmended = prev.filter(t =>
          !amended.find(a => a.date === t.date && a.venue === t.venue)
        );
        return [...withoutAmended, ...amended.flatMap(compressOrder)];
      });
    }

    const changed = asArray.filter(r => {
      const old = requests.find(o => o.id === r.id);
      if (!old) return false;
      return (
        old.status !== r.status ||
        old.notes  !== r.notes  ||
        JSON.stringify(old.items) !== JSON.stringify(r.items)
      );
    });

    for (const r of changed) {
      await supabase.from('requests').update({
        status:        r.status,
        items:         r.items,
        notes:         r.notes || null,
        sent_at:       r.sentAt     || r.sent_at     || null,
        received_at:   r.receivedAt || r.received_at || null,
        delivery_date: r.delivery_date || null,
      }).eq('id', r.id);
    }
    setRequestsRaw(asArray);
  }

  async function handleStockLog(logs) {
    for (const log of logs) {
      await supabase.from('stock_log').insert(log);
    }
    setStockLog(prev => [...prev, ...logs]);
    setProducts(prev => prev.map(product => {
      const entry = logs.find(l => l.product === product.name);
      return entry ? { ...product, stock: product.stock + entry.quantity } : product;
    }));
  }

  function handleChecklistSubmit(entry) {
    setChecklistLog(prev => [entry, ...prev]);
  }

  function handleParOrderSubmit(order) {
    setRequestsRaw(prev => [...prev, order]);
    navigate('transfers');
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setCurrentUser(null);
    window.history.replaceState({}, '', '/');
  }

  // ── Early returns — public routes (bypass auth + loading entirely) ─────────
  if (isAllergensPublic)                                         return <AllergensPublic />;
  if (isEventEnquiry)                                            return <EventEnquiry />;
  if (window.location.pathname.startsWith('/set-password'))      return <SetPassword />;
  if (window.location.pathname.startsWith('/governance/share'))  return <ShareholderView />;
  if (onboardingMatch)  return <NewHireForm token={onboardingMatch[1]} />;
  if (welcomeMatch)     return <Onboarding />;
  if (balanceMatch)     return <BalancePage />;
  if (warningMatch)     return <Warning />;

  if (authLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f0' }}>
      <p style={{ color: '#888' }}>Loading…</p>
    </div>
  );

  if (!currentUser) return <Login onLogin={setCurrentUser} />;

  // ── Pages ─────────────────────────────────────────────────────────────────
  const role = currentUser.role;

  const pages = {
    home: <Homepage currentUser={currentUser} onNavigate={navigate} products={products} requests={requests} />,

    checklist: <Checklist
                 currentUser={currentUser}
                 products={products}
                 requests={requests}
                 onChecklistUpdate={handleChecklistSubmit}
                 isAdmin={false}
                 onNavigate={navigate}
               />,

    checklists: ['admin', 'manager'].includes(role)
      ? <ChecklistAdmin currentUser={currentUser} />
      : null,

    'data-entry': role === 'admin'
      ? <DataEntryPage currentUser={currentUser} />
      : null,

    fridgetemps: <FridgeTempsPage currentUser={currentUser} />,

    products: ['admin', 'manager'].includes(role)
      ? <Products products={products} setProducts={setProductsSafe} />
      : null,

    'product-order': ['admin', 'manager'].includes(role)
      ? <ProductOrder products={products} currentUser={currentUser} />
      : null,

    users: role === 'admin'
      ? <UserManagement currentUser={currentUser} />
      : null,

    settings: ['admin', 'manager'].includes(role)
      ? <SettingsPage currentUser={currentUser} />
      : null,

    'data-sync': role === 'admin'
      ? <DataSync currentUser={currentUser} />
      : null,

    reviews: <ReviewsPage currentUser={currentUser} />,

    orders: <Orders
              requests={requests}
              setRequests={handleRequestsUpdate}
              currentUser={currentUser}
              products={products}
              prefilledCart={prefilledCart}
              setPrefilledCart={setPrefilledCart}
              parMap={(() => {
                const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                const now = new Date();
                if (now.getHours() < 2) now.setDate(now.getDate() - 1);
                const dayStr = DAYS[now.getDay()];
                const venue  = currentUser?.venue || '';
                return parLevels?.[venue]?.[dayStr] || {};
              })()}
              onNavigate={navigate}
              onCartCountChange={setOrderCartCount}
            />,

    transfers:   <Transfers requests={requests} setRequests={handleRequestsUpdate} currentUser={currentUser} onNavigate={navigate} />,
    maintenance: <MaintenancePage currentUser={currentUser} />,
    allergens:   <Allergens currentUser={currentUser} />,

    stocktake: <Stock
                 products={products}
                 currentUser={currentUser}
                 requests={requests}
                 setRequests={handleRequestsUpdate}
                 onNavigate={navigate}
                 setPrefilledCart={setPrefilledCart}
               />,

    reports: <Reports transferLog={transferLog} checklistLog={checklistLog} currentUser={currentUser} />,

    results: ['admin', 'manager', 'advisor'].includes(role)
      ? <ResultsPage currentUser={currentUser} />
      : null,

    finance: role === 'admin'
      ? <FinancePage currentUser={currentUser} />
      : null,

    'group-results': ['admin', 'advisor'].includes(role)
      ? <GroupResults currentUser={currentUser} />
      : null,

    governance: ['admin', 'advisor'].includes(role)
      ? <GovernancePage currentUser={currentUser} />
      : null,

    cashup: role === 'admin'
      ? <CashUpPage currentUser={currentUser} />
      : null,

    shifty: ['admin', 'manager', 'supervisor'].includes(role)
      ? <ShiftyPage currentUser={currentUser} />
      : null,

    'kitchen-shifty': ['admin', 'manager', 'supervisor', 'kitchen'].includes(role)
      ? <KitchenShiftyPage currentUser={currentUser} />
      : null,

    events: <Events currentUser={currentUser} />,

    team:     <Team currentUser={currentUser} />,
    training: <TrainingPage currentUser={currentUser} />,

    calendar: <CalendarPage
                user={currentUser}
                userRole={currentUser.role}
                userVenue={currentUser.venue}
                userVenues={currentUser.venues || []}
              />,

    docs: <DocsPage
            user={currentUser}
            userRole={currentUser.role}
            userVenue={currentUser.venue}
            userVenues={currentUser.venues || []}
          />,
  };

  const isMobile = window.innerWidth < 768;

  const FloatingLemon = () => (
    <img src={lemonSliceIcon} alt="Menu" style={{ width: 30, height: 30, objectFit: 'contain' }} />
  );

  return (
    <div className="app" style={{ position: 'relative' }}>
      {/* Floating lemon toggle — mobile only */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(p => !p)}
          style={{
            position: 'fixed', top: 12, left: 12, zIndex: 1100,
            width: 44, height: 44, borderRadius: '50%',
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform 0.15s',
          }}
          aria-label="Toggle menu"
        >
          <FloatingLemon />
        </button>
      )}

      {/* Sidebar — desktop always visible, mobile only when open */}
      {(!isMobile || sidebarOpen) && (
        <Sidebar
          activePage={activePage}
          onNavigate={navigate}
          currentUser={currentUser}
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(p => !p)}
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
          orderCartCount={orderCartCount}
        />
      )}

      <main className="main-content" style={{
        ...(isMobile ? { marginLeft: 0, paddingTop: '60px' } : undefined),
        ...(activePage === 'home' ? { background: '#EAF4F8', minHeight: '100vh' } : {}),
      }}>
        {activePage && pages[activePage]}
      </main>
    </div>
  );
}

// ── Cash Up page wrapper — weekly grid + annual report tabs ──────────────────
function CashUpPage({ currentUser }) {
  const [tab, setTab] = useState('weekly');
  const C = { charcoal:'#2F3A42', navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', white:'#FFFFFF', cream:'#F7F6EF' };
  return (
    <div style={{ padding:'1.25rem', maxWidth:'1060px', margin:'0 auto', background:C.cream, minHeight:'100vh' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'1.25rem' }}>
        {[{key:'weekly',label:'Weekly Grid'},{key:'report',label:'Annual Report'}].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding:'0.42rem 1rem', borderRadius:'9px', fontSize:'0.82rem', fontWeight:700, cursor:'pointer', border:`2px solid ${tab===t.key ? C.navy : C.border}`, background: tab===t.key ? C.navy : C.white, color: tab===t.key ? C.white : C.mid, transition:'all 0.12s' }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'weekly' && <CashUpTab currentUser={currentUser} isAdmin={true} />}
      {tab === 'report' && <CashReportTab currentUser={currentUser} />}
    </div>
  );
}

export default App;